import { Controller } from "@hotwired/stimulus";
import { DirectUpload } from "@rails/activestorage";

/**
 * direct-upload (Stimulus controller)
 *
 * But:
 * - permettre l'upload d'une image directement depuis le navigateur vers S3
 *   via Active Storage Direct Upload, sans dépendance externe (pas de
 *   FilePond, pas de ResizeObserver). On reste sur un `<input type="file">`
 *   natif enrichi par une drop-zone CSS et un retour visuel (progress + preview).
 *
 * Markup attendu (cf. app/views/admin/source_configs/edit_logo.html.erb):
 *
 *   <div data-controller="direct-upload"
 *        data-direct-upload-url-value="<%= rails_direct_uploads_url %>"
 *        data-direct-upload-signed-id-target-id-value="source_logo_signed_id"
 *        data-direct-upload-max-file-size-value="5242880">
 *     <label class="direct-upload-dropzone"
 *            data-direct-upload-target="dropzone"
 *            data-action="dragover->direct-upload#dragover
 *                         dragleave->direct-upload#dragleave
 *                         drop->direct-upload#drop">
 *       <span data-direct-upload-target="placeholder">Glissez une image ici ou parcourir</span>
 *       <img class="direct-upload-preview" hidden data-direct-upload-target="preview" />
 *       <input type="file" class="sr-only" accept="image/*"
 *              data-direct-upload-target="input"
 *              data-action="change->direct-upload#fileChosen" />
 *     </label>
 *     <div class="direct-upload-progress" hidden data-direct-upload-target="progress">
 *       <div class="direct-upload-progress-bar" data-direct-upload-target="progressBar"></div>
 *     </div>
 *     <div class="direct-upload-error text-danger small" data-direct-upload-target="error"></div>
 *   </div>
 *
 * Comportement:
 * - Click sur la drop-zone -> ouvre le picker (le label englobe l'input file natif).
 * - Drag&drop d'un fichier sur la drop-zone -> idem.
 * - À la sélection: validation (type + taille), preview locale, lancement
 *   `DirectUpload` avec barre de progression. Quand le blob est créé,
 *   on écrit le `signed_id` dans le hidden field pour que le submit du form
 *   n'envoie qu'une simple string.
 */
export default class extends Controller {
  static targets = ["dropzone", "input", "placeholder", "preview", "progress", "progressBar", "error"];

  static values = {
    url: String,
    signedIdTargetId: String,
    maxFileSize: { type: Number, default: 5 * 1024 * 1024 },
    acceptedTypes: { type: String, default: "image/png,image/jpeg,image/gif,image/svg+xml,image/webp" }
  };

  fileChosen(event) {
    const file = event.target.files && event.target.files[0];
    if (file) this._upload(file);
  }

  dragover(event) {
    event.preventDefault();
    if (this.hasDropzoneTarget) this.dropzoneTarget.classList.add("is-dragover");
  }

  dragleave(event) {
    event.preventDefault();
    if (this.hasDropzoneTarget) this.dropzoneTarget.classList.remove("is-dragover");
  }

  drop(event) {
    event.preventDefault();
    if (this.hasDropzoneTarget) this.dropzoneTarget.classList.remove("is-dragover");
    const file = event.dataTransfer && event.dataTransfer.files && event.dataTransfer.files[0];
    if (file) {
      if (this.hasInputTarget) {
        try {
          const dt = new DataTransfer();
          dt.items.add(file);
          this.inputTarget.files = dt.files;
        } catch (_) { /* Safari < 14.1 ne supporte pas DataTransfer ctor */ }
      }
      this._upload(file);
    }
  }

  _upload(file) {
    this._clearError();
    if (!this._validateType(file)) {
      this._showError(`Type non supporté (${file.type || "inconnu"}).`);
      return;
    }
    if (this.maxFileSizeValue && file.size > this.maxFileSizeValue) {
      const mb = Math.round(this.maxFileSizeValue / (1024 * 1024));
      this._showError(`Fichier trop lourd (${this._humanSize(file.size)}, max ${mb} Mo).`);
      return;
    }

    this._showPreview(file);
    this._showProgress(0);

    const upload = new DirectUpload(file, this.urlValue, {
      directUploadWillStoreFileWithXHR: (xhr) => {
        xhr.upload.addEventListener("progress", (event) => {
          if (event.lengthComputable) {
            this._showProgress(Math.round((event.loaded / event.total) * 100));
          }
        });
      }
    });

    upload.create((err, blob) => {
      this._hideProgress();
      if (err) {
        const detail = (err.message && err.message.trim()) || "";
        this._showError(detail ? `Erreur lors de l'upload : ${detail}` : "Erreur lors de l'upload (vérifier la console et la config CORS du bucket S3).");
        return;
      }
      this._writeSignedId(blob.signed_id);
    });
  }

  _validateType(file) {
    const accepted = this.acceptedTypesValue.split(",").map((s) => s.trim()).filter(Boolean);
    if (accepted.length === 0) return true;
    return accepted.some((pattern) => {
      if (pattern.endsWith("/*")) {
        const prefix = pattern.slice(0, -1);
        return file.type.startsWith(prefix);
      }
      return file.type === pattern;
    });
  }

  _showPreview(file) {
    if (!this.hasPreviewTarget) return;
    if (!file.type.startsWith("image/")) return;
    const reader = new FileReader();
    reader.onload = () => {
      this.previewTarget.src = reader.result;
      this.previewTarget.hidden = false;
      if (this.hasPlaceholderTarget) this.placeholderTarget.hidden = true;
    };
    reader.readAsDataURL(file);
  }

  _showProgress(percent) {
    if (!this.hasProgressTarget) return;
    this.progressTarget.hidden = false;
    if (this.hasProgressBarTarget) {
      this.progressBarTarget.style.width = `${percent}%`;
      this.progressBarTarget.setAttribute("aria-valuenow", String(percent));
    }
  }

  _hideProgress() {
    if (this.hasProgressTarget) this.progressTarget.hidden = true;
  }

  _showError(message) {
    if (this.hasErrorTarget) this.errorTarget.textContent = message;
  }

  _clearError() {
    if (this.hasErrorTarget) this.errorTarget.textContent = "";
  }

  _writeSignedId(value) {
    const target = document.getElementById(this.signedIdTargetIdValue);
    if (target) target.value = value || "";
  }

  _humanSize(bytes) {
    if (bytes < 1024) return `${bytes} o`;
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} ko`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
  }
}
